import { ComplianceObj, EmpDepartment, Unit } from '../config/interfaces';

const isPercentLessThanOne = (numerator: number, denominator: number) =>
  !Number.isNaN(numerator) &&
  !Number.isNaN(denominator) &&
  numerator > 0 &&
  denominator > 0 &&
  (numerator * 100) / denominator < 1;

const isPercentRoundedToOneHundred = (numerator: number, denominator: number) =>
  !Number.isNaN(numerator) &&
  !Number.isNaN(denominator) &&
  numerator > 0 &&
  denominator > 0 &&
  (numerator * 100) / denominator > 99.5 &&
  (numerator * 100) / denominator < 100;

const calculateComplianceNumbers = (compliance: any, roundingType?: string) => {
  if (roundingType === 'employee') {
    const roundedPct =
      Math.round((compliance.rounded / compliance.total) * 100) || 0;
    const nonePct = Math.round((compliance.none / compliance.total) * 100) || 0;
    return {
      total: compliance.total,
      none: compliance.none,
      nonePct,
      rounded: compliance.rounded,
      roundedPct
    };
  }
  const roundedTotal = compliance.rounded + compliance.unable;
  const census = roundedTotal + compliance.none;
  const roundedPct = Math.round((roundedTotal / census) * 100) || 0; // Future: const roundedPct = Math.round((compliance.rounded / roundedTotal) * 100) || 0;
  const unablePct = Math.round((compliance.unable / roundedTotal) * 100) || 0;
  const nonePct = Math.round((compliance.none / census) * 100) || 0;
  return {
    census,
    roundedTotal,
    none: compliance.none,
    nonePct,
    rounded: compliance.rounded,
    roundedPct,
    unable: compliance.unable,
    unablePct
  };
};

const addComplianceNumbers = (
  prevData: any,
  newData: any,
  roundingType?: string
) => {
  if (roundingType === 'employee') {
    const none = prevData.none + newData.none;
    const rounded = prevData.rounded + newData.rounded;
    const total = prevData.total + newData.total;
    const roundedPct = isPercentLessThanOne(Number(rounded), Number(total))
      ? 1
      : isPercentRoundedToOneHundred(Number(rounded), Number(total))
      ? 99
      : Math.round((rounded / total) * 100) || 0; // Future: const roundedPct = Math.round((rounded / roundedTotal) * 100) || 0;
    const nonePct = isPercentLessThanOne(Number(none), Number(total))
      ? 1
      : isPercentRoundedToOneHundred(Number(none), Number(total))
      ? 99
      : Math.round((none / total) * 100) || 0;
    return Object.assign(prevData, {
      total,
      none,
      nonePct,
      rounded,
      roundedPct
    });
  }
  const none = prevData.none + newData.none;
  const rounded = prevData.rounded + newData.rounded;
  const unable = prevData.unable + newData.unable;
  const roundedTotal = rounded + unable;
  const census = roundedTotal + none;
  const roundedPct = isPercentLessThanOne(Number(roundedTotal), Number(census))
    ? 1
    : isPercentRoundedToOneHundred(Number(roundedTotal), Number(census))
    ? 99
    : Math.round((roundedTotal / census) * 100) || 0; // Future: const roundedPct = Math.round((rounded / roundedTotal) * 100) || 0;
  const unablePct = isPercentLessThanOne(Number(unable), Number(roundedTotal))
    ? 1
    : isPercentRoundedToOneHundred(Number(unable), Number(roundedTotal))
    ? 99
    : Math.round((unable / roundedTotal) * 100) || 0;
  const nonePct = isPercentLessThanOne(Number(none), Number(census))
    ? 1
    : isPercentRoundedToOneHundred(Number(none), Number(census))
    ? 99
    : Math.round((none / census) * 100) || 0;
  return Object.assign(prevData, {
    census,
    none,
    nonePct,
    rounded,
    roundedTotal,
    roundedPct,
    unable,
    unablePct
  });
};

const calculateTotalCompliance = (
  compliance: any,
  unit: string,
  unitId: string,
  roundingType?: string
) => {
  if (roundingType === 'employee') {
    return compliance.reduce(
      (acc: any, deptCompliance: any) => {
        return {
          department: unit,
          departmentId: unit,
          unitType: '',
          total: deptCompliance.total + acc.total,
          roundedPercent: isPercentLessThanOne(
            Number(deptCompliance.roundedNumber + acc.roundedNumber),
            Number(deptCompliance.total + acc.total)
          )
            ? 1
            : Math.round(
                ((deptCompliance.roundedNumber + acc.roundedNumber) * 100) /
                  (deptCompliance.total + acc.total)
              ) || 0,
          roundedNumber: deptCompliance.roundedNumber + acc.roundedNumber,
          notRoundedPercent: isPercentLessThanOne(
            Number(deptCompliance.notRoundedNumber + acc.notRoundedNumber),
            Number(deptCompliance.total + acc.total)
          )
            ? 1
            : isPercentRoundedToOneHundred(
                Number(deptCompliance.notRoundedNumber + acc.notRoundedNumber),
                Number(deptCompliance.total + acc.total)
              )
            ? 99
            : Math.round(
                ((deptCompliance.notRoundedNumber + acc.notRoundedNumber) *
                  100) /
                  (deptCompliance.total + acc.total)
              ) || 0,
          notRoundedNumber:
            deptCompliance.notRoundedNumber + acc.notRoundedNumber
        };
      },
      {
        department: 'All',
        departmentId: 'All',
        unitType: '',
        total: 0,
        roundedPercent: 0,
        roundedNumber: 0,
        notRoundedPercent: 0,
        notRoundedNumber: 0
      }
    );
  }
  return compliance.reduce(
    (acc: any, unitCompliance: any) => {
      return {
        unit,
        unitId,
        unitType: '',
        census: unitCompliance.census + acc.census,
        roundedPercent: isPercentLessThanOne(
          Number(unitCompliance.roundedNumber + acc.roundedNumber),
          Number(unitCompliance.census + acc.census)
        )
          ? 1
          : Math.round(
              ((unitCompliance.roundedNumber + acc.roundedNumber) * 100) /
                (unitCompliance.census + acc.census)
            ) || 0,
        roundedNumber: unitCompliance.roundedNumber + acc.roundedNumber, // Future: possibly change to just rounded Yes
        unablePercent: isPercentLessThanOne(
          Number(unitCompliance.unableNumber + acc.unableNumber),
          Number(unitCompliance.roundedNumber + acc.roundedNumber)
        )
          ? 1
          : Math.round(
              ((unitCompliance.unableNumber + acc.unableNumber) * 100) /
                (unitCompliance.roundedNumber + acc.roundedNumber)
            ) || 0,
        unableNumber: unitCompliance.unableNumber + acc.unableNumber,
        notRoundedPercent: isPercentLessThanOne(
          Number(unitCompliance.notRoundedNumber + acc.notRoundedNumber),
          Number(unitCompliance.census + acc.census)
        )
          ? 1
          : isPercentRoundedToOneHundred(
              Number(unitCompliance.notRoundedNumber + acc.notRoundedNumber),
              Number(unitCompliance.census + acc.census)
            )
          ? 99
          : Math.round(
              ((unitCompliance.notRoundedNumber + acc.notRoundedNumber) * 100) /
                (unitCompliance.census + acc.census)
            ) || 0,
        notRoundedNumber: unitCompliance.notRoundedNumber + acc.notRoundedNumber
      };
    },
    {
      unit: 'All',
      unitId: 'All',
      unitType: '',
      census: 0,
      roundedPercent: 0,
      roundedNumber: 0, // Future: possibly change to just rounded Yes
      unablePercent: 0,
      unableNumber: 0,
      notRoundedPercent: 0,
      notRoundedNumber: 0
    }
  );
};

const reduceComplianceByUnit = (
  compliance: ComplianceObj[],
  unitsById: Unit[]
) => {
  return compliance.reduce((prev: any, curr: any) => {
    const { unitId } = curr;
    const prevUnit = prev[unitId];
    const unitType = (locType: string) => {
      switch (locType) {
        case 'O':
          return 'Outpatient';
        case 'I':
          return 'Inpatient';
        case 'BH':
          return 'Behavioral Health';
        default:
          return 'ED';
      }
    };
    const dayData = {
      dt: curr.dt,
      facilityId: curr.facilityId,
      unitId: curr.unitId,
      locType: curr.locType,
      locTypeDisplay: unitType(curr.locType),
      ...calculateComplianceNumbers(curr)
    };

    // If we've already created a property for this unit
    if (prevUnit) {
      // Push this day into the data for that unit
      prevUnit.data.push(dayData);
      // Combine the data from this day with the combinedData for that unit
      prevUnit.combinedData = addComplianceNumbers(
        prevUnit.combinedData,
        calculateComplianceNumbers(dayData)
      );
      // Otherwise, create a property for this unit and initialize with group
      // info, starting data, and starting combinedData
    } else {
      /* eslint-disable no-param-reassign */
      const filteredUnit = unitsById.filter(
        (unit: Unit) => unit.unitId === unitId
      );
      const currentUnit: Unit =
        filteredUnit.length > 0 ? filteredUnit[0] : unitsById[0];

      prev[currentUnit.unitId] = {
        groupId: currentUnit.unitId,
        groupName: currentUnit.unit,
        groupType: 'unit',
        locTypeDisplay:
          curr.locType === 'O'
            ? 'Outpatient'
            : curr.locType === 'I'
            ? 'Inpatient'
            : curr.locType === 'BH'
            ? 'Behavioral Health'
            : 'ED',
        data: [dayData],
        combinedData: calculateComplianceNumbers(dayData)
      };
    }

    return prev;
  }, {});
};

const reduceComplianceByDept = (
  compliance: ComplianceObj[],
  empDepartments: EmpDepartment[]
) => {
  return compliance.reduce((prev: any, curr: any) => {
    const { departmentId } = curr;
    const prevDept = prev[departmentId];
    const dayData = {
      dt: curr.dt,
      facilityId: curr.facilityId,
      departmentId: curr.departmentId,
      locType: curr.locType,
      ...calculateComplianceNumbers(curr, 'employee')
    };
    // TODO: do we still need this if statement?
    // If we've already created a property for this department
    if (prevDept) {
      // Push this day into the data for that department
      prevDept.data.push(dayData);
      // Combine the data from this day with the combinedData for that department
      prevDept.combinedData = addComplianceNumbers(
        prevDept.combinedData,
        calculateComplianceNumbers(dayData, 'employee'),
        'employee'
      );
    } else {
      // Otherwise, create a property for this department and initialize with group
      prev[curr.departmentId] = {
        groupId: curr.departmentId,
        // Looking for emp department name to match the dropdown filter
        groupName: empDepartments.find(
          dept => dept.departmentId === curr.departmentId
        )?.departmentName,
        // referencing database name here in case we need to ref later
        locTypeDisplay: curr.department,
        groupType: 'dept',
        data: [dayData],
        combinedData: calculateComplianceNumbers(dayData, 'employee')
      };
    }

    return prev;
  }, {});
};

export {
  isPercentLessThanOne,
  isPercentRoundedToOneHundred,
  calculateTotalCompliance,
  reduceComplianceByUnit,
  reduceComplianceByDept
};
